import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import type { CreditsPurchaseProduct } from '@/app/types';
import { cn } from '@/app/utils';
import CreditsItem from './components/CreditsItem';
import PurchaseCreditsFooter from './components/PurchaseCreditsFooter';

export const PurchaseCreditsContent: FC<{
  fromAgentProject?: boolean;
  source?: string;
  loadingProducts: boolean;
  error?: Error;
  products: CreditsPurchaseProduct[];
}> = ({ fromAgentProject, source, loadingProducts, error, products }) => {
  const t = useTranslations();

  const [currentProduct, setCurrentProduct] = useState<CreditsPurchaseProduct | null>(null);
  // 响应刷新后同步派生本轮可购买商品。即使 effect 尚未更新 state，也不会把已下架、
  // 已锁券或已切换实验分组的旧商品继续传给支付入口。
  const availableCurrentProduct = products.find((product) => product.id === currentProduct?.id) ?? products[0] ?? null;

  useEffect(() => {
    setCurrentProduct(availableCurrentProduct);
  }, [products]);

  if (loadingProducts && !products?.length) {
    return (
      <div className="flex h-[440px] items-center justify-center">
        <Loading className="animate-spin text-3xl" />
      </div>
    );
  }

  if (!products?.length) {
    return (
      <div className="flex h-[440px] items-center justify-center">
        <div>{error ? t('payments.networkError') : t('popup.dialog.purchaseCredits.empty')}</div>
      </div>
    );
  }

  return (
    <>
      <div className={cn('flex flex-col justify-center text-sm', source === 'my-subscription' ? 'px-0 pt-6' : 'px-3')}>
        {source !== 'my-subscription' && !fromAgentProject && (
          <div className="px-2 py-1 sm:text-center">{t('popup.dialog.purchaseCredits.title')}</div>
        )}
        <motion.div
          className="grid grid-cols-3 items-end gap-4 pb-4 sm:gap-2"
          initial="hidden"
          animate="visible"
          variants={cardsContainerVariants}
        >
          {products.map((product) => (
            <motion.div key={product.id} variants={cardItemVariants}>
              <CreditsItem
                product={product}
                active={product.id === availableCurrentProduct?.id}
                setCurrentProduct={setCurrentProduct}
              />
            </motion.div>
          ))}
        </motion.div>
        <div className="whitespace-pre-wrap text-system-white36">{t('popup.dialog.purchaseCredits.description')}</div>
      </div>
      <PurchaseCreditsFooter currentProduct={availableCurrentProduct} source={source} />
    </>
  );
};
